import { type JSX } from 'react';
import { MediaQueryAttributeInput } from '@repo/common-utils/mediaQueryAttributeInputHelper';
import { useLocale } from '@repo/i18n';
import { useNativeMemberCardProducts } from '@repo/widget-utils/services/hooks/native-membership';
import { useMembershipMode } from '@repo/widget-utils/widgetsConfiguration';
import { ProductCard2 } from 'src/components/domain/product-card-2/ProductCard2';
import { ProductCardInfo } from 'src/components/domain/product-card-2/ProductCardInfo';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import ProductCardListContainer from 'src/widgets/activities/product/product-list-2/product-card-list-2/ProductCardListContainer';
import ProductCardListItem from 'src/widgets/activities/product/product-list-2/product-card-list-2/ProductCardListItem';
import { resolveNativeText } from './nativeText';

type MemberCardProductGridProps = {
    ids: string[]; // optional filter/order by member-card-product id; empty = show all
    urls: string[];
    numElements: MediaQueryAttributeInput;
    scroll: boolean;
};

// Discovery grid for native member-card products. Mirrors the value-card grids but feeds
// NativeMemberCardProduct: prices are whole NOK (no /100), there is no product type or campaign,
// and the hero image comes from the product's image_url (Base). Renders nothing if not native.
export default function MemberCardProductGrid({
    ids,
    urls,
    numElements,
    scroll,
}: MemberCardProductGridProps): JSX.Element | null {
    const { locale } = useLocale();
    const { products } = useNativeMemberCardProducts();
    const isNative = useMembershipMode() === 'native';
    const customizations = useCustomizations();

    if (!isNative) return null;

    const shown =
        ids.length > 0
            ? products
                  .filter((p) => ids.includes(p.id))
                  .toSorted((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id))
            : products;

    return (
        <ProductCardListContainer
            numElements={numElements}
            scroll={scroll}
            productListPadding={customizations.productListPadding}
        >
            {shown.map((product, _i, arr) => {
                const index = ids.indexOf(product.id);
                const url = (index >= 0 ? urls[index] : urls[0]) ?? '';
                return (
                    <ProductCardListItem
                        key={product.id}
                        numElements={numElements}
                        scroll={scroll}
                        numProductCatalogs={arr.length}
                    >
                        <ProductCard2
                            url={url}
                            image={{ url: product.image_url ?? undefined }}
                            disableImageConversion
                            body={
                                <ProductCardInfo
                                    url={url}
                                    title={resolveNativeText(product.name, locale)}
                                    description={resolveNativeText(product.description, locale)}
                                    fromPrice={product.price}
                                    showFrom={false}
                                />
                            }
                        />
                    </ProductCardListItem>
                );
            })}
        </ProductCardListContainer>
    );
}
